import SaveOutlined from "@mui/icons-material/SaveOutlined";
import { Button } from "@mui/material";
import { ReactNode, useEffect, useRef } from "react";

export type SaveButtonProps = {
  loading?: boolean;
  loadingText?: string;
  disabled?: boolean;
  endIcon?: ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  saveText?: string;
  startIcon?: ReactNode;
  hideStartIcon?: boolean;
  formId?: string;
};

export const SaveButton: React.FC<SaveButtonProps> = ({
  loading: isLoading,
  loadingText = "Saving...",
  disabled: isDisabled,
  onClick,
  formId,
  saveText = "Save",
  startIcon = <SaveOutlined />,
  hideStartIcon,
  endIcon,
}) => {
  // Double-click guard: blocks repeated clicks before React re-renders with
  // isLoading=true. Only active when the caller passes isLoading —
  // callers without it aren't performing async mutations so double-click
  // protection isn't needed, and we'd have no signal to reset the ref.
  const hasLoadingProp = isLoading !== undefined;
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!isLoading) inFlightRef.current = false;
  }, [isLoading]);

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    if (hasLoadingProp && inFlightRef.current) {
      e.preventDefault();
      return;
    }
    inFlightRef.current = true;
    onClick?.(e);
  };

  return (
    <Button
      type="submit"
      variant="contained"
      disabled={isDisabled || isLoading}
      startIcon={!hideStartIcon && !isLoading && startIcon}
      endIcon={endIcon && !isLoading ? endIcon : undefined}
      onClick={handleClick}
      form={formId}
    >
      {isLoading ? loadingText : saveText}
    </Button>
  );
};
